import React, { useState } from "react";

import { linkDocumentAnalyses } from "@services/document-analysis";
import DocumentIntakeModal from "./document-intake-modal";
import DocumentIntakeWizard from "./document-intake-wizard";
import { getWizardProfile } from "./document-intake-profiles";

/**
 * Interceptor de flujo, en dos modos:
 *  - `requestDocuments(profileKey, next)`: asistente por pasos del alta de un caso (el primer
 *    documento trae la fecha de nacimiento y de ella depende la identificacion que se pide). Llama
 *    `next(analyses, { birthdate, minor, manualBirthdate })` al terminar.
 *  - `requestDocumentList(requirements, next, title)`: modal con una lista dada (p. ej. solo los
 *    documentos que faltan). Llama `next(analyses)`.
 * Cancelar no llama `next`. Un perfil desconocido llama `next([])` de inmediato.
 *
 * Monta `intakeModal` una vez en el JSX de la pantalla.
 */
export const useDocumentIntake = () => {
  const [pending, setPending] = useState(null);

  const requestDocumentList = (requirements, next, title) => {
    if (requirements.length === 0) {
      next([]);
      return;
    }
    setPending({ requirements, next, title });
  };

  const requestDocuments = (profileKey, next) => {
    if (!getWizardProfile(profileKey)) {
      next([], {});
      return;
    }
    setPending({ profileKey, next });
  };

  const finish = (...args) => {
    const { next } = pending;
    setPending(null);
    next(...args);
  };

  let intakeModal = null;
  if (pending && pending.profileKey) {
    intakeModal = (
      <DocumentIntakeWizard
        profile={pending.profileKey}
        onCancel={() => setPending(null)}
        onFinish={finish}
      />
    );
  } else if (pending) {
    intakeModal = (
      <DocumentIntakeModal
        show
        title={pending.title}
        requirements={pending.requirements}
        onCancel={() => setPending(null)}
        onFinish={finish}
      />
    );
  }

  return { requestDocuments, requestDocumentList, intakeModal };
};

/**
 * Liga los analisis al caso ya creado. Nunca rechaza: un fallo aqui no debe detener la
 * navegacion del usuario (los documentos ya quedaron guardados, solo sin caso).
 */
export const linkAnalysesToCase = (caseSlug, analyses) => {
  if (!caseSlug || !analyses || analyses.length === 0) {
    return Promise.resolve();
  }
  return linkDocumentAnalyses(
    caseSlug,
    analyses.map((a) => a.analysisUuid)
  ).catch(() => {});
};
